import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CompetitionModel} from '../../../models/competition/competition.model';
import {EVOListViewColumn} from '../../../models/UI/EVOListViewColumn.model';

@Component({
  selector: 'app-evo-competition-list-view',
  templateUrl: './evo-competition-list-view.component.html',
  styleUrls: ['./evo-competition-list-view.component.css']
})
export class EvoCompetitionListViewComponent implements OnInit {

  /**
   * Fixed or dynamic
   */
  @Input() heightType = 'fixed';

  /**
   * Used if height type is fixed.
   */
  @Input() height = '500px';

  @Input() competitions: CompetitionModel[] = [];

  allSelected = false;
  @Input() selectedCompetitions: CompetitionModel[] = [];

  @Input() hasMoreData = false;

  @Output() competitionsSelected = new EventEmitter<CompetitionModel[]>();
  @Output() fetchMore = new EventEmitter();

  @Input() sort = new EVOCompetitionListSort();
  @Output() sortChanged = new EventEmitter<EVOCompetitionListSort>();

  @Input() selectionColumn = EVOListViewColumn.xsFixedColumn('selection', false);
  @Input() nameColumn = EVOListViewColumn.largeGrowColumn('name');
  @Input() typeColumn = EVOListViewColumn.smallFixedColumn('type');
  @Input() statusColumn = EVOListViewColumn.smallFixedColumn('status');
  @Input() startAtColumn = EVOListViewColumn.smallFixedColumn('startAt');
  @Input() endAtColumn = EVOListViewColumn.smallFixedColumn('endAt');
  @Input() createdAtColumn = EVOListViewColumn.smallFixedColumn('createdAt');

  @Input() competitionClickEnabled = false;
  @Output() competitionClicked = new EventEmitter<CompetitionModel>();

  /** Shows a "View" button on each row that opens the competition the way a user sees it. */
  @Input() viewEnabled = false;
  @Output() competitionViewClicked = new EventEmitter<CompetitionModel>();

  @Input('selectionEnabled')
  set selectionEnabled(selection: boolean) {
    this.selectionColumn.isVisible = selection;
  }

  getSelectionEnabled(): boolean {
    return this.selectionColumn.isVisible;
  }

  constructor() { }

  ngOnInit() {
  }

  getContainerHeight() {

    if (this.heightType === 'fixed') {
      return this.height;
    }

    return 'auto';

  }

  getContentHeight() {

    if (this.heightType === 'fixed') {
      return 'calc(100% - 38px)';
    }

    return 'auto';

  }

  getYOverflow(): string {

    if (this.heightType === 'fixed') {
      return 'scroll';
    }

    return 'auto';
  }

  onAllSelected(selection: boolean) {

    if (selection) {
      this.selectedCompetitions = this.competitions;
    } else {
      this.selectedCompetitions = [];
    }

    this.allSelected = selection;

    if (this.competitionsSelected) {
      this.competitionsSelected.emit(this.selectedCompetitions);
    }

  }

  onCompetitionSelected(competition: CompetitionModel) {

    const index = this.selectedCompetitions.indexOf(competition);
    if (index !== -1) {
      this.selectedCompetitions.splice(index, 1);
    } else {
      this.selectedCompetitions.push(competition);
    }

    if (this.competitionsSelected) {
      this.competitionsSelected.emit(this.selectedCompetitions);
    }

  }

  hasSelectedCompetition(competition: CompetitionModel): boolean {

    return this.selectedCompetitions.includes(competition);

  }

  onFetchMoreData() {
    if (this.fetchMore) {
      this.fetchMore.emit();
    }
  }

  onNameLegendSortClicked() {

    if (this.sort.key !== 'name') {

      this.sort.key = 'name';
      this.sort.value = -1;

    } else {

      if (this.sort.value === 1) {
        this.sort.value = -1;
      } else {
        this.sort.value = 1;
      }

    }

    if (this.sortChanged) {
      this.sortChanged.emit(this.sort);
    }

  }

  onStartAtLegendSortClicked() {

    if (this.sort.key !== 'startAt') {

      this.sort.key = 'startAt';
      this.sort.value = -1;

    } else {

      if (this.sort.value === 1) {
        this.sort.value = -1;
      } else {
        this.sort.value = 1;
      }

    }

    if (this.sortChanged) {
      this.sortChanged.emit(this.sort);
    }

  }

  onEndAtLegendSortClicked() {

    if (this.sort.key !== 'endAt') {

      this.sort.key = 'endAt';
      this.sort.value = -1;

    } else {

      if (this.sort.value === 1) {
        this.sort.value = -1;
      } else {
        this.sort.value = 1;
      }

    }

    if (this.sortChanged) {
      this.sortChanged.emit(this.sort);
    }

  }

  onCompetitionClicked(competition: CompetitionModel) {

    if (this.competitionClickEnabled === true && this.competitionClicked) {
      this.competitionClicked.emit(competition);
    }

  }

  onCompetitionViewClicked(competition: CompetitionModel, event: Event) {

    event.stopPropagation();
    this.competitionViewClicked.emit(competition);

  }

  getSortArrowImgUrl(): string {

    if (this.sort.value === 1) {
      return './assets/icons/sort-up.svg';
    } else {
      return './assets/icons/sort-down.svg';
    }

  }

  calcWidth(size: string): string {

    return EVOListViewColumn.calcWidth(size, this.getColumnDescriptors());

  }

  getColumnDescriptors(): EVOListViewColumn[] {

    return [
      this.selectionColumn,
      this.nameColumn,
      this.typeColumn,
      this.statusColumn,
      this.startAtColumn,
      this.endAtColumn,
      this.createdAtColumn
    ];

  }

  getCompetitionStatus(competition: CompetitionModel): string {
    if (competition.isActive()) {
      return 'Active';
    } else if (competition.isUpcoming()) {
      return 'Upcoming';
    } else if (competition.isFinished()) {
      return 'Finished';
    }
    return 'Unknown';
  }

  getCompetitionStatusClass(competition: CompetitionModel): string {
    if (competition.isActive()) {
      return 'greenColor';
    } else if (competition.isUpcoming()) {
      return 'blueColor';
    } else if (competition.isFinished()) {
      return 'grayColor';
    }
    return 'grayColor';
  }

}

export class EVOCompetitionListSort {
  /**
   * The key to use for sorting
   */
  key = 'name';
  /**
   * Should be 1 or -1 for ascending or descending
   */
  value = 1;
}
